import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { employeeAPI, sessionAPI } from '../services/api';
import AppHeader from './AppHeader';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  position: '',
  medical_due_date: '',
  phone: '',
  mobile_phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  notes: '',
};

const EmployeeEditForm = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (isNew) {
          const res = await sessionAPI.getCurrentSession();
          if (!cancelled) setSessionId(res.data?.id);
        } else {
          const res = await employeeAPI.getEmployee(id);
          const d = res?.data || {};
          const rawDate = d.medical_due_date;
          const medicalDate = rawDate ? String(rawDate).slice(0, 10) : '';
          if (!cancelled) {
            setFormData({
              first_name: d.first_name ?? '',
              last_name: d.last_name ?? '',
              email: d.email ?? '',
              position: d.position ?? '',
              medical_due_date: medicalDate,
              phone: d.phone ?? '',
              mobile_phone: d.mobile_phone ?? '',
              address: d.address ?? '',
              city: d.city ?? '',
              state: d.state ?? '',
              zip_code: d.zip_code ?? '',
              notes: d.notes ?? '',
              version: d.version ?? 1,
            });
            setSessionId(d.session);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!(formData?.first_name ?? '').trim() || !(formData?.last_name ?? '').trim()) {
      setError('First name and last name are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...formData };
      if (!payload.medical_due_date) payload.medical_due_date = null;
      if (isNew) delete payload.version;
      console.log('Saving employee...', payload);
      let res;
      if (isNew) {
        payload.session = sessionId;
        res = await employeeAPI.createEmployee(payload);
      } else {
        res = await employeeAPI.updateEmployee(id, payload);
      }
      console.log('Save response:', res?.status, res?.data);
      if (res && res.status >= 200 && res.status < 300) {
        setTimeout(() => navigate(isNew ? `/employees/${res.data?.id ?? ''}` : `/employees/${id}`), 0);
        return;
      }
    } catch (err) {
      if (err.response || err.isAxiosError) {
        const detail = err.response?.data ? JSON.stringify(err.response.data) : err.message;
        setError(`Error saving employee: ${detail}`);
      }
      setSaving(false);
    }
  };

  return (
    <Box>
      <AppHeader backTo="/employees" />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {isNew ? 'Add Employee' : 'Edit Employee'}
          </Typography>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Loading…</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="subtitle1" color="primary" sx={{ mt: 1, mb: 0.5 }}>Contact &amp; role</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="First Name" value={formData?.first_name ?? ''} onChange={(e) => handleChange('first_name', e.target.value)} fullWidth required />
            <TextField label="Last Name" value={formData?.last_name ?? ''} onChange={(e) => handleChange('last_name', e.target.value)} fullWidth required />
            <TextField label="Email" type="email" value={formData?.email ?? ''} onChange={(e) => handleChange('email', e.target.value)} fullWidth />
            <TextField label="Position" value={formData?.position ?? ''} onChange={(e) => handleChange('position', e.target.value)} fullWidth />
            <TextField label="Home Phone" value={formData?.phone ?? ''} onChange={(e) => handleChange('phone', e.target.value)} fullWidth />
            <TextField label="Mobile Phone" value={formData?.mobile_phone ?? ''} onChange={(e) => handleChange('mobile_phone', e.target.value)} fullWidth />
          </Box>

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 0.5 }}>Address</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Address" value={formData?.address ?? ''} onChange={(e) => handleChange('address', e.target.value)} fullWidth multiline minRows={1} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="City" value={formData?.city ?? ''} onChange={(e) => handleChange('city', e.target.value)} sx={{ flex: '1 1 200px' }} />
              <TextField label="State" value={formData?.state ?? ''} onChange={(e) => handleChange('state', e.target.value)} sx={{ width: 120 }} />
              <TextField label="Zip" value={formData?.zip_code ?? ''} onChange={(e) => handleChange('zip_code', e.target.value)} sx={{ width: 120 }} />
            </Box>
          </Box>

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 0.5 }}>Medical</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Medical Due Date" type="date" value={formData?.medical_due_date ?? ''} onChange={(e) => handleChange('medical_due_date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Box>

          <Typography variant="subtitle1" color="primary" sx={{ mt: 2, mb: 0.5 }}>Notes</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Notes" value={formData?.notes ?? ''} onChange={(e) => handleChange('notes', e.target.value)} fullWidth multiline rows={3} />
          </Box>

          <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<Cancel />} onClick={() => navigate(id ? `/employees/${id}` : '/employees')}>Cancel</Button>
            <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving...' : 'Save'}</Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EmployeeEditForm;
